/* =============================================================================
 *  WallOptionsModal — the "⋯" options surface for the Wall.
 *
 *  First section: which email categories surface as cards. The category list is
 *  the owner's mail folders (sender-derived) + 'Inbox' (uncategorised). Unchecking
 *  a category hides it from the wall (it stays in the inbox). Built to grow — add
 *  more <section>s here for future wall settings.
 * ============================================================================= */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useEmailPanel } from '../../pages/mail/contexts/EmailPanelContext';
import { useWallOptions, listEmailCategories } from './wallOptions';

const WallOptionsModal: React.FC<{ show: boolean; onClose: () => void }> = ({ show, onClose }) => {
  const { customFolders } = useEmailPanel();
  const { options, save } = useWallOptions();
  const categories = useMemo(() => listEmailCategories(customFolders), [customFolders]);

  // Local draft so Cancel discards; seeded only on the OPEN transition. A
  // background options change (e.g. a second tab saving) must not stomp the
  // user's in-progress toggles, so we read the latest via a ref, not a dep.
  const [hidden, setHidden] = useState<string[]>(options.hiddenEmailCategories);
  const latestHidden = useRef(options.hiddenEmailCategories);
  latestHidden.current = options.hiddenEmailCategories;
  useEffect(() => { if (show) setHidden(latestHidden.current); }, [show]);

  const toggle = (cat: string) =>
    setHidden((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));
  const apply = () => { save({ ...options, hiddenEmailCategories: hidden }); onClose(); };

  const shownCount = categories.length - hidden.filter((c) => categories.includes(c)).length;

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-5 fw-bold">Wall options</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-baseline justify-content-between mb-1">
          <div className="fw-semibold">Emails on the wall</div>
          <div className="text-muted fs-8">{shownCount} of {categories.length} showing</div>
        </div>
        <div className="text-muted fs-8 mb-3">
          Choose which email categories surface as cards. Unchecked categories stay in your inbox but won’t clutter the wall.
        </div>
        <div className="d-flex flex-column gap-1" style={{ maxHeight: 340, overflowY: 'auto' }}>
          {categories.map((cat) => {
            const isShown = !hidden.includes(cat);
            return (
              <label
                key={cat}
                className="gwopt-row d-flex align-items-center gap-2 p-2 rounded"
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  className="form-check-input m-0 flex-shrink-0"
                  checked={isShown}
                  onChange={() => toggle(cat)}
                />
                <span className={`fw-semibold ${isShown ? 'text-gray-800' : 'text-muted'}`}>{cat}</span>
                {cat === 'Inbox' && <span className="badge badge-light-secondary fs-9 ms-1">uncategorised</span>}
              </label>
            );
          })}
          {categories.length <= 1 && (
            <div className="text-muted fs-8 py-2">
              No custom email categories yet — they appear here as you create sender folders in the mail client.
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={apply}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WallOptionsModal;
